import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams } from 'types';
import { PaginatedRevenuePerMemberReportList } from 'types/generated';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseRevenuePerMember {
  month: string | null;
  type: 'total' | 'paying';
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[] | null)[];
}

function useRevenuePerMember({
  month,
  type,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUseRevenuePerMember): UseInfiniteQueryResult<AxiosResponse<PaginatedRevenuePerMemberReportList>> {
  const fetchRevenuePerMember = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedRevenuePerMemberReportList>> => {
      const params = pageParam;

      if (month) {
        params.month = month;
      }
      params.type = type;

      if (ordering) {
        params.ordering = ordering
          .replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
          .replace('.', () => '__')
          .concat(',pk');
      }

      return client.get(`reports/revenue-per-member?${encodeUrlParams(params)}`);
    },
    [limit, ordering, month, type],
  );

  return useInfiniteQuery(['revenue-per-member', month, type, ordering, csvExport], fetchRevenuePerMember, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: !!month && enabled,
    staleTime: 86400000, // 1 day
  });
}

export default useRevenuePerMember;
